/**
 * Admin Pricing & Promotions Management Page
 * Manage pricing rules, promotions, and discount codes
 */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { 
  Link2, 
  ArrowLeft, 
  DollarSign,
  RefreshCw,
  Plus,
  Trash2,
  Tag,
  Percent,
  Gift,
  Calendar,
  X,
  CheckCircle,
  XCircle
} from 'lucide-react';

const AdminPricingManagement = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState({ pricing_rules: [], promotions: [], discount_codes: [] });
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('rules');
  const [showAddModal, setShowAddModal] = useState(false);
  const [modalType, setModalType] = useState('rule');
  const [actionLoading, setActionLoading] = useState(null);
  
  // Forms
  const [newRule, setNewRule] = useState({
    name: '', type: 'flat', value: 0, min_order: 0, applies_to: 'all'
  });
  const [newPromo, setNewPromo] = useState({
    name: '', description: '', discount_type: 'percentage', discount_value: 0, min_purchase: 0, start_date: '', end_date: ''
  });
  const [newCode, setNewCode] = useState({
    code: '', description: '', discount_type: 'percentage', discount_value: 0, min_purchase: 0, max_uses: 100, valid_from: '', valid_until: ''
  });

  useEffect(() => {
    if (!isAdmin()) {
      toast.error('Access denied');
      navigate('/dashboard');
      return;
    }
    fetchPricing();
  }, [isAdmin, navigate]);

  const fetchPricing = async () => {
    setLoading(true);
    try {
      const response = await api.get('/admin/pricing/');
      setData(response.data || { pricing_rules: [], promotions: [], discount_codes: [] });
    } catch (error) {
      console.error('Error fetching pricing:', error);
      toast.error('Failed to load pricing data');
    } finally {
      setLoading(false);
    }
  };

  const openAddModal = (type) => {
    setModalType(type);
    setShowAddModal(true);
  };

  const handleAddRule = async () => {
    if (!newRule.name) {
      toast.error('Rule name is required');
      return;
    }
    setActionLoading('add-rule');
    try {
      await api.post('/admin/pricing/rules', newRule);
      toast.success('Pricing rule created');
      setShowAddModal(false);
      setNewRule({ name: '', type: 'flat', value: 0, min_order: 0, applies_to: 'all' });
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create rule');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddPromo = async () => {
    if (!newPromo.name) {
      toast.error('Promotion name is required');
      return;
    }
    setActionLoading('add-promo');
    try {
      await api.post('/admin/pricing/promotions', newPromo);
      toast.success('Promotion created');
      setShowAddModal(false);
      setNewPromo({ name: '', description: '', discount_type: 'percentage', discount_value: 0, min_purchase: 0, start_date: '', end_date: '' });
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create promotion');
    } finally {
      setActionLoading(null);
    }
  };

  const handleAddCode = async () => {
    if (!newCode.code) {
      toast.error('Discount code is required');
      return;
    }
    setActionLoading('add-code');
    try {
      await api.post('/admin/pricing/discount-codes', newCode);
      toast.success('Discount code created');
      setShowAddModal(false);
      setNewCode({ code: '', description: '', discount_type: 'percentage', discount_value: 0, min_purchase: 0, max_uses: 100, valid_from: '', valid_until: '' });
      fetchPricing();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create discount code');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteRule = async (ruleId) => {
    if (!window.confirm('Delete this pricing rule?')) return;
    setActionLoading(ruleId);
    try {
      await api.delete(`/admin/pricing/rules/${ruleId}`);
      toast.success('Pricing rule deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete rule');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeletePromo = async (promoId) => {
    if (!window.confirm('Delete this promotion?')) return;
    setActionLoading(promoId);
    try {
      await api.delete(`/admin/pricing/promotions/${promoId}`);
      toast.success('Promotion deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete promotion');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDeleteCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    setActionLoading(codeId);
    try {
      await api.delete(`/admin/pricing/discount-codes/${codeId}`);
      toast.success('Discount code deleted');
      fetchPricing();
    } catch (error) {
      toast.error('Failed to delete discount code');
    } finally {
      setActionLoading(null);
    }
  };

  const tabs = [
    { id: 'rules', label: 'Pricing Rules', icon: DollarSign, count: data.pricing_rules.length },
    { id: 'promos', label: 'Promotions', icon: Gift, count: data.promotions.length },
    { id: 'codes', label: 'Discount Codes', icon: Tag, count: data.discount_codes.length },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="border-b border-slate-800 bg-slate-900/50 backdrop-blur-sm sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button
              onClick={() => navigate('/admin')}
              variant="ghost"
              size="sm"
              className="text-slate-400 hover:text-white hover:bg-slate-800"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="flex items-center gap-3">
              <Link2 className="h-7 w-7 text-amber-500" />
              <span className="text-xl font-bold text-white tracking-tight">Liantiao</span>
              <span className="text-xs font-mono text-green-400 bg-green-500/10 px-2 py-1 rounded border border-green-500/20">
                PRICING
              </span>
            </div>
          </div>
          <Button
            onClick={fetchPricing}
            variant="outline"
            size="sm"
            className="border-slate-700 text-slate-300 hover:bg-slate-800"
            disabled={loading}
            data-testid="refresh-btn"
          >
            <RefreshCw className={`h-4 w-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Tabs */}
        <div className="flex gap-2 mb-8 border-b border-slate-800 pb-4">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-all ${
                  activeTab === tab.id 
                    ? 'bg-amber-500 text-slate-950' 
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                data-testid={`tab-${tab.id}`}
              >
                <Icon className="h-4 w-4" />
                {tab.label}
                <span className={`text-xs px-1.5 py-0.5 rounded ${
                  activeTab === tab.id ? 'bg-amber-600' : 'bg-slate-700'
                }`}>
                  {tab.count}
                </span>
              </button>
            );
          })}
        </div>

        {/* Pricing Rules Tab */}
        {activeTab === 'rules' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-amber-400" />
                Pricing Rules
              </h2>
              <Button onClick={() => openAddModal('rule')} className="bg-teal-500 text-white hover:bg-teal-400" data-testid="add-rule-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Rule
              </Button>
            </div>
            
            <div className="grid gap-4">
              {data.pricing_rules.length === 0 ? (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-8 text-center text-slate-400">
                  No pricing rules configured. Create one to get started.
                </div>
              ) : (
                data.pricing_rules.map((rule) => (
                  <div key={rule.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4" data-testid={`rule-${rule.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-green-500/10 border border-green-500/20 flex items-center justify-center">
                          <DollarSign className="h-6 w-6 text-green-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{rule.name}</p>
                          <p className="text-slate-400 text-sm">
                            {rule.type === 'flat' ? `$${rule.value}` : `${rule.value}%`} off • 
                            Min order: ${rule.min_order} • 
                            Applies to: {rule.applies_to}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs border ${rule.is_active ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                          {rule.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Button onClick={() => handleDeleteRule(rule.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" disabled={actionLoading === rule.id}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Promotions Tab */}
        {activeTab === 'promos' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Gift className="h-5 w-5 text-purple-400" />
                Active Promotions
              </h2>
              <Button onClick={() => openAddModal('promo')} className="bg-purple-500 text-white hover:bg-purple-400" data-testid="add-promo-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Promotion
              </Button>
            </div>
            
            <div className="grid gap-4">
              {data.promotions.length === 0 ? (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-8 text-center text-slate-400">
                  No promotions active. Create one to attract customers.
                </div>
              ) : (
                data.promotions.map((promo) => (
                  <div key={promo.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4" data-testid={`promo-${promo.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-purple-500/10 border border-purple-500/20 flex items-center justify-center">
                          <Gift className="h-6 w-6 text-purple-400" />
                        </div>
                        <div>
                          <p className="text-white font-medium">{promo.name}</p>
                          <p className="text-slate-400 text-sm">{promo.description}</p>
                          <p className="text-slate-500 text-xs mt-1">
                            {promo.discount_type === 'percentage' ? `${promo.discount_value}%` : `$${promo.discount_value}`} off • 
                            Uses: {promo.uses_count || 0}/{promo.max_uses || '∞'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs border ${promo.is_active ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                          {promo.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Button onClick={() => handleDeletePromo(promo.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" disabled={actionLoading === promo.id}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {/* Discount Codes Tab */}
        {activeTab === 'codes' && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold text-white flex items-center gap-2">
                <Tag className="h-5 w-5 text-cyan-400" />
                Discount Codes
              </h2>
              <Button onClick={() => openAddModal('code')} className="bg-cyan-500 text-white hover:bg-cyan-400" data-testid="add-code-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Code
              </Button>
            </div>
            
            <div className="grid gap-4">
              {data.discount_codes.length === 0 ? (
                <div className="bg-slate-900/50 border border-slate-800 rounded-lg p-8 text-center text-slate-400">
                  No discount codes created. Add one for customers to use at checkout.
                </div>
              ) : (
                data.discount_codes.map((code) => (
                  <div key={code.id} className="bg-slate-900/50 border border-slate-800 rounded-lg p-4" data-testid={`code-${code.id}`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="w-12 h-12 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center">
                          <Tag className="h-6 w-6 text-cyan-400" />
                        </div>
                        <div>
                          <p className="text-white font-mono font-bold text-lg">{code.code}</p>
                          <p className="text-slate-400 text-sm">{code.description}</p>
                          <p className="text-slate-500 text-xs mt-1">
                            {code.discount_type === 'percentage' ? `${code.discount_value}%` : `$${code.discount_value}`} off • 
                            Uses: {code.uses_count || 0}/{code.max_uses || '∞'} •
                            Min: ${code.min_purchase || 0}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded-full text-xs border ${code.is_active ? 'bg-teal-500/20 text-teal-400 border-teal-500/30' : 'bg-slate-500/20 text-slate-400 border-slate-500/30'}`}>
                          {code.is_active ? 'Active' : 'Inactive'}
                        </span>
                        <Button onClick={() => handleDeleteCode(code.id)} variant="ghost" size="sm" className="text-red-400 hover:bg-red-500/10" disabled={actionLoading === code.id}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        )}
      </main>

      {/* Add Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-slate-900 border border-slate-800 rounded-lg w-full max-w-md p-6" data-testid="add-modal">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-xl font-bold text-white flex items-center gap-2">
                {modalType === 'rule' && <><DollarSign className="h-5 w-5 text-green-400" /> Add Pricing Rule</>}
                {modalType === 'promo' && <><Gift className="h-5 w-5 text-purple-400" /> Add Promotion</>}
                {modalType === 'code' && <><Tag className="h-5 w-5 text-cyan-400" /> Add Discount Code</>}
              </h2>
              <Button onClick={() => setShowAddModal(false)} variant="ghost" size="sm" className="text-slate-400">
                <X className="h-5 w-5" />
              </Button>
            </div>
            
            {/* Pricing Rule Form */}
            {modalType === 'rule' && (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Rule Name *</Label>
                  <Input value={newRule.name} onChange={(e) => setNewRule({ ...newRule, name: e.target.value })} placeholder="e.g., Free Delivery Over $50" className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Type</Label>
                    <select value={newRule.type} onChange={(e) => setNewRule({ ...newRule, type: e.target.value })} className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2">
                      <option value="flat">Flat Amount ($)</option>
                      <option value="percentage">Percentage (%)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Value</Label>
                    <Input type="number" value={newRule.value} onChange={(e) => setNewRule({ ...newRule, value: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <div>
                  <Label className="text-slate-300">Minimum Order ($)</Label>
                  <Input type="number" value={newRule.min_order} onChange={(e) => setNewRule({ ...newRule, min_order: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Applies To</Label>
                  <select value={newRule.applies_to} onChange={(e) => setNewRule({ ...newRule, applies_to: e.target.value })} className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2">
                    <option value="all">All Orders</option>
                    <option value="delivery">Delivery Only</option>
                    <option value="products">Products Only</option>
                  </select>
                </div>
                <Button onClick={handleAddRule} className="w-full bg-green-500 text-white hover:bg-green-400" disabled={actionLoading === 'add-rule'}>
                  {actionLoading === 'add-rule' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Rule
                </Button>
              </div>
            )}

            {/* Promotion Form */}
            {modalType === 'promo' && (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Promotion Name *</Label>
                  <Input value={newPromo.name} onChange={(e) => setNewPromo({ ...newPromo, name: e.target.value })} placeholder="e.g., Summer Sale" className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div>
                  <Label className="text-slate-300">Description</Label>
                  <Input value={newPromo.description} onChange={(e) => setNewPromo({ ...newPromo, description: e.target.value })} placeholder="20% off all items" className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Discount Type</Label>
                    <select value={newPromo.discount_type} onChange={(e) => setNewPromo({ ...newPromo, discount_type: e.target.value })} className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2">
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Value</Label>
                    <Input type="number" value={newPromo.discount_value} onChange={(e) => setNewPromo({ ...newPromo, discount_value: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Start Date</Label>
                    <Input type="date" value={newPromo.start_date} onChange={(e) => setNewPromo({ ...newPromo, start_date: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300">End Date</Label>
                    <Input type="date" value={newPromo.end_date} onChange={(e) => setNewPromo({ ...newPromo, end_date: e.target.value })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <Button onClick={handleAddPromo} className="w-full bg-purple-500 text-white hover:bg-purple-400" disabled={actionLoading === 'add-promo'}>
                  {actionLoading === 'add-promo' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Promotion
                </Button>
              </div>
            )}

            {/* Discount Code Form */}
            {modalType === 'code' && (
              <div className="space-y-4">
                <div>
                  <Label className="text-slate-300">Code *</Label>
                  <Input value={newCode.code} onChange={(e) => setNewCode({ ...newCode, code: e.target.value.toUpperCase() })} placeholder="e.g., SAVE20" className="bg-slate-800 border-slate-700 text-white font-mono" />
                </div>
                <div>
                  <Label className="text-slate-300">Description</Label>
                  <Input value={newCode.description} onChange={(e) => setNewCode({ ...newCode, description: e.target.value })} placeholder="Save 20% on your order" className="bg-slate-800 border-slate-700 text-white" />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Discount Type</Label>
                    <select value={newCode.discount_type} onChange={(e) => setNewCode({ ...newCode, discount_type: e.target.value })} className="w-full bg-slate-800 border border-slate-700 text-white rounded-md p-2">
                      <option value="percentage">Percentage (%)</option>
                      <option value="fixed">Fixed Amount ($)</option>
                    </select>
                  </div>
                  <div>
                    <Label className="text-slate-300">Value</Label>
                    <Input type="number" value={newCode.discount_value} onChange={(e) => setNewCode({ ...newCode, discount_value: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-300">Min Purchase ($)</Label>
                    <Input type="number" value={newCode.min_purchase} onChange={(e) => setNewCode({ ...newCode, min_purchase: parseFloat(e.target.value) || 0 })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                  <div>
                    <Label className="text-slate-300">Max Uses</Label>
                    <Input type="number" value={newCode.max_uses} onChange={(e) => setNewCode({ ...newCode, max_uses: parseInt(e.target.value) || 100 })} className="bg-slate-800 border-slate-700 text-white" />
                  </div>
                </div>
                <Button onClick={handleAddCode} className="w-full bg-cyan-500 text-white hover:bg-cyan-400" disabled={actionLoading === 'add-code'}>
                  {actionLoading === 'add-code' ? <RefreshCw className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                  Create Discount Code
                </Button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPricingManagement;
